import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Grid,
  Layout,
  Row,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  SnippetsOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import axios from "axios";
import { responseNotification } from "../../utils/notify";
import Loader from "../common/Loader";
import styles from "../../styles/tailwind/List.module.css";
import moment from "moment";

const { Content } = Layout;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

type SettlementStatus = "REQUESTED" | "PROCESSING" | "PAID" | "FAILED";

interface SettlementOrderItem {
  key: string;
  orderId: string;
  date: string;
  totalAmount: number;
  netReceivable: number;
}

interface SettlementBreakdownItem {
  label: string;
  amount: number;
  negative?: boolean;
  highlight?: boolean;
  bold?: boolean;
}

interface SettlementDetailsData {
  storeName: string;
  requestedAt: string;
  requestedFinalPayout: number;
  payableTo: string;
  noOfOrders: number;
  settlementId: string;
  fulfillmentBy: string;
  status: SettlementStatus;
  orders: SettlementOrderItem[];
  customerPayment: SettlementBreakdownItem[];
  merchantSettlement: SettlementBreakdownItem[];
}

const formatCurrency = (amount: number, withPrefix = false): string => {
  const formatted = new Intl.NumberFormat("en-BD").format(amount);
  return withPrefix ? `Tk. ${formatted}` : formatted;
};

const getStatusTagColor = (status: SettlementStatus): string => {
  switch (status) {
    case "REQUESTED":
      return "blue";
    case "PROCESSING":
      return "orange";
    case "PAID":
      return "green";
    case "FAILED":
      return "red";
    default:
      return "default";
  }
};

const BreakdownRow = ({
  label,
  amount,
  negative,
  highlight,
  bold,
}: SettlementBreakdownItem): ReactElement => {
  const amountColor = highlight
    ? "#fa8c16"
    : negative
    ? "#ff4d4f"
    : "rgba(0,0,0,0.88)";
  const fontWeight = bold || highlight ? 600 : 400;

  return (
    <Row
      justify="space-between"
      align="middle"
      style={{ marginBottom: 12, gap: 12 }}
    >
      <Text
        style={{
          fontWeight,
          color: "rgba(0,0,0,0.88)",
          flex: 1,
        }}
      >
        {label}
      </Text>

      <Text
        style={{
          fontWeight,
          color: amountColor,
          textAlign: "right",
          minWidth: 90,
        }}
      >
        {negative ? "-" : ""}
        {formatCurrency(Math.abs(amount))}
      </Text>
    </Row>
  );
};

const InfoRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}): ReactElement => {
  return (
    <Row
      justify="space-between"
      align="top"
      style={{ marginBottom: 12, gap: 12 }}
    >
      <Text style={{ color: "rgba(0,0,0,0.65)" }}>{label}</Text>
      <Text strong style={{ textAlign: "right" }}>
        {value}
      </Text>
    </Row>
  );
};

const BreakdownCard = ({
  title,
  items,
}: {
  title: string;
  items: SettlementBreakdownItem[];
}): ReactElement => {
  return (
    <Card
      title={
        <Text strong style={{ fontSize: 16 }}>
          {title}
        </Text>
      }
      bordered={false}
      style={{
        borderRadius: 16,
        boxShadow: "0 6px 24px rgba(0,0,0,0.06)",
      }}
      bodyStyle={{ padding: 20 }}
    >
      {items.map((item, index) => (
        <BreakdownRow
          key={`${item.label}-${index}`}
          label={item.label}
          amount={item.amount}
          negative={item.negative}
          highlight={item.highlight}
          bold={item.bold}
        />
      ))}
    </Card>
  );
};

const SettlementDetailsPage = (): ReactElement => {
  const screens = useBreakpoint();
  const { token } = useSelector((state) => (state as any)?.authReducer);
  const route = useParams();
  const orderID = (route as any)?.orderId;
  const [singleOrderInfo, setSingleOrderInfo] = useState<any>({
    loading: false,
    data: null,
  });
  const fetchOrderDetails = useCallback(
    (getOrderId: any) => {
      if (!getOrderId) return;

      try {
        setSingleOrderInfo({ loading: true, data: undefined });
        axios
          .get(
            `${process.env.REACT_APP_ORDER_API}/merchant-settlement/requested-settlement-from-shop-order-details?shopSettlementRequestId=${getOrderId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            },
          )
          .then((data) => {
            if (data.status === 200) {
              setSingleOrderInfo({
                loading: false,
                data: data?.data,
              });
            } else {
              setSingleOrderInfo({ loading: false, data: null });
              responseNotification(
                data.statusText || "something went wrong",
                "error",
              );
            }
          })
          .catch(() => {
            setSingleOrderInfo({ loading: false, data: null });
          });
      } catch {
        setSingleOrderInfo({ loading: false, data: null });
      }
    },
    [token],
  );

  useEffect(() => {
    fetchOrderDetails(orderID);
  }, [fetchOrderDetails, orderID]);

  const settlement = singleOrderInfo?.data?.shopSettlementRequest || {};

  const settlementData: SettlementDetailsData = useMemo(
    () => ({
      storeName: "Jachai Flagship Store",
      requestedAt: "09 Mar 2026, 06:52 PM",
      requestedFinalPayout: 1466,
      payableTo: "JACHAI",
      noOfOrders: 2,
      settlementId: "J-2932373413345",
      fulfillmentBy: "JACHAI",
      status: "REQUESTED",
      orders: [
        {
          key: "1",
          orderId: "J-0394704645",
          date: "09 Mar 2026, 06:52 PM",
          totalAmount: 1466,
          netReceivable: 1200,
        },
        {
          key: "2",
          orderId: "J-0394704645",
          date: "09 Mar 2026, 06:52 PM",
          totalAmount: 1466,
          netReceivable: 1200,
        },
      ],
    }),
    [],
  );

  const columns: ColumnsType<SettlementOrderItem> = [
    {
      title: "Order ID",
      dataIndex: "orderId",
      key: "orderId",
      width: 180,
      render: (value: string) => <Text strong>{value}</Text>,
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 220,
      responsive: ["md"],
    },
    {
      title: "Total Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      align: "right",
      width: 140,
      render: (value: number) => formatCurrency(value),
    },
    {
      title: "Net Receivable",
      dataIndex: "netReceivable",
      key: "netReceivable",
      align: "right",
      width: 150,
      render: (value: number) => <Text strong>{formatCurrency(value)}</Text>,
    },
  ];

  const handleBack = (): void => {
    window.history.back();
  };

  const handleDownload = (): void => {
    console.log("Download settlement");
  };

  return (
    <Layout style={{ background: "#f5f7fb", minHeight: "100vh" }}>
      <Content style={{ padding: screens.xs ? 12 : 24 }}>
        <div style={{ maxWidth: 1400, margin: "0 auto" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 20,
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            {/* LEFT */}
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={handleBack}
                style={{ borderRadius: 10 }}
              />

              <div>
                <Title level={screens.xs ? 4 : 3} style={{ margin: 0 }}>
                  Settlement Details
                </Title>
                <Text style={{ color: "rgba(0,0,0,0.55)" }}>
                  Settlement details and payout breakdown
                </Text>
              </div>
            </div>

            {/* RIGHT */}
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownload}
              size="large"
              style={{
                borderRadius: 10,
                minWidth: 140,
              }}
            >
              Download
            </Button>
          </div>
          <Row gutter={[20, 20]}>
            <Col xs={24} xl={8}>
              <Card
                bordered={false}
                style={{
                  borderRadius: 16,
                  boxShadow: "0 6px 24px rgba(0,0,0,0.06)",
                  marginBottom: 20,
                }}
                bodyStyle={{ padding: 20 }}
              >
                <Space direction="vertical" size={14} style={{ width: "100%" }}>
                  <Tag
                    color={getStatusTagColor(settlementData.status)}
                    style={{
                      width: "fit-content",
                      borderRadius: 999,
                      paddingInline: 12,
                      paddingBlock: 4,
                      fontWeight: 600,
                    }}
                  >
                    {settlementData.status}
                  </Tag>

                  <div>
                    <Text style={{ color: "rgba(0,0,0,0.55)" }}>
                      Requested Final Payout
                    </Text>
                    <Title
                      level={2}
                      style={{
                        margin: "6px 0 4px",
                        color: "#fa8c16",
                        lineHeight: 1.2,
                      }}
                    >
                      &#2547;
                      {parseInt(
                        singleOrderInfo?.data?.shopSettlementRequest
                          ?.totalNetReceivableAmount || 0,
                      )}
                    </Title>
                  </div>

                  <div>
                    <Title level={4} style={{ margin: 0 }}>
                      {singleOrderInfo?.data?.shopSettlementRequest?.shopName}
                    </Title>
                    <Text style={{ color: "rgba(0,0,0,0.55)" }}>
                      {moment(
                        singleOrderInfo?.data?.shopSettlementRequest?.createdAt,
                      ).format("DD MMM YYYY, hh:mm A")}
                    </Text>
                  </div>

                  <Divider style={{ margin: "8px 0" }} />

                  <div>
                    <InfoRow
                      label="Payable To"
                      value={
                        singleOrderInfo?.data?.shopSettlementRequest?.payableTo
                      }
                    />
                    <InfoRow
                      label="No. of Orders"
                      value={
                        singleOrderInfo?.data?.shopSettlementRequest?.orderIds
                          ?.length || 0
                      }
                    />
                    <InfoRow
                      label="Settlement ID"
                      value={
                        singleOrderInfo?.data?.shopSettlementRequest
                          ?.settlementOrderId
                      }
                    />
                    <InfoRow
                      label="Fulfilment By"
                      value={
                        singleOrderInfo?.data?.shopSettlementRequest
                          ?.deliveryFulfillBy
                      }
                    />
                  </div>
                </Space>
              </Card>

              <div style={{ display: "grid", gap: 20 }}>
                <Card
                  bordered={false}
                  style={{
                    borderRadius: 16,
                    boxShadow: "0 6px 24px rgba(0,0,0,0.06)",
                    marginBottom: 20,
                  }}
                  bodyStyle={{ padding: 20 }}
                >
                  <Space
                    direction="vertical"
                    size={14}
                    style={{ width: "100%" }}
                  >
                    <div>
                      <Title level={4} style={{ margin: 0 }}>
                        Customer Payment
                      </Title>
                      <Text style={{ color: "rgba(0,0,0,0.55)" }}>
                        Customer payment breakdown
                      </Text>
                    </div>

                    <Divider style={{ margin: "8px 0" }} />

                    <div>
                      <InfoRow
                        label="Subtotal"
                        value={`৳${singleOrderInfo?.data?.shopSettlementRequest?.totalSubtotalAmount}`}
                      />
                      <InfoRow
                        label="Discount"
                        value={
                          <span style={{ color: "#ff4d4f" }}>
                            -৳
                            {singleOrderInfo?.data?.shopSettlementRequest
                              ?.totalDiscountAmount || 0}
                          </span>
                        }
                      />
                      <InfoRow
                        label="Product After Discount"
                        value={
                          <Text strong>
                            ৳
                            {singleOrderInfo?.data?.shopSettlementRequest
                              ?.totalSubtotalAmount -
                              singleOrderInfo?.data?.shopSettlementRequest
                                ?.totalDiscountAmount}
                          </Text>
                        }
                      />
                      <InfoRow
                        label="Delivery Charge"
                        value={`৳${
                          singleOrderInfo?.data?.shopSettlementRequest
                            ?.totalDeliveryCharge || 0
                        }`}
                      />
                      <InfoRow
                        label="Delivery Tips"
                        value={`৳${
                          singleOrderInfo?.data?.shopSettlementRequest
                            ?.totalDeliveryTips || 0
                        }`}
                      />
                      <InfoRow
                        label="Service Fee"
                        value={`৳${
                          singleOrderInfo?.data?.shopSettlementRequest
                            ?.serviceFee || 0
                        }`}
                      />
                      <InfoRow
                        label="Tax Fee"
                        value={`৳${
                          singleOrderInfo?.data?.shopSettlementRequest
                            ?.taxFee || 0
                        }`}
                      />
                      <InfoRow
                        label="COD Fee"
                        value={`৳${
                          singleOrderInfo?.data?.shopSettlementRequest
                            ?.codFee || 0
                        }`}
                      />
                    </div>

                    <Divider style={{ margin: "8px 0" }} />

                    <Row justify="space-between" align="middle">
                      <Text strong style={{ fontSize: 16 }}>
                        Total Customer Pay
                      </Text>
                      <Text strong style={{ fontSize: 18, color: "#fa8c16" }}>
                        ৳
                        {singleOrderInfo?.data?.shopSettlementRequest?.amount ||
                          0}
                      </Text>
                    </Row>
                  </Space>
                </Card>

                <Card
                  bordered={false}
                  style={{
                    borderRadius: 16,
                    boxShadow: "0 6px 24px rgba(0,0,0,0.06)",
                    marginBottom: 20,
                  }}
                  bodyStyle={{ padding: 20 }}
                >
                  <Space
                    direction="vertical"
                    size={14}
                    style={{ width: "100%" }}
                  >
                    <div>
                      <Title level={4} style={{ margin: 0 }}>
                        Merchant Settlement
                      </Title>
                      <Text style={{ color: "rgba(0,0,0,0.55)" }}>
                        Merchant settlement breakdown
                      </Text>
                    </div>

                    <Divider style={{ margin: "8px 0" }} />

                    <div>
                      <InfoRow
                        label="Customer Paid"
                        value={
                          <Text strong>
                            ৳
                            {singleOrderInfo?.data?.shopSettlementRequest
                              ?.amount || 0}
                          </Text>
                        }
                      />
                      <InfoRow
                        label="Platform Commission"
                        value={
                          <span style={{ color: "#ff4d4f" }}>
                            -৳
                            {singleOrderInfo?.data?.shopSettlementRequest
                              ?.totalPlatformCommissionAmount || 0}
                          </span>
                        }
                      />
                      <InfoRow
                        label="Delivery Charge"
                        value={
                          <span style={{ color: "#ff4d4f" }}>
                            -৳
                            {singleOrderInfo?.data?.shopSettlementRequest
                              ?.totalDeliveryCharge || 0}
                          </span>
                        }
                      />
                      <InfoRow
                        label="Delivery Tips"
                        value={
                          <span style={{ color: "#ff4d4f" }}>
                            -৳
                            {singleOrderInfo?.data?.shopSettlementRequest
                              ?.totalDeliveryTips || 0}
                          </span>
                        }
                      />
                      <InfoRow
                        label="Service Fee"
                        value={
                          <span style={{ color: "#ff4d4f" }}>
                            -৳
                            {singleOrderInfo?.data?.shopSettlementRequest
                              ?.totalServiceFee || 0}
                          </span>
                        }
                      />
                      <InfoRow
                        label="Tax Fee"
                        value={
                          <span style={{ color: "#ff4d4f" }}>
                            -৳
                            {singleOrderInfo?.data?.shopSettlementRequest
                              ?.totalTaxFee || 0}
                          </span>
                        }
                      />
                      <InfoRow
                        label="COD Fee"
                        value={
                          <span style={{ color: "#ff4d4f" }}>
                            -৳
                            {singleOrderInfo?.data?.shopSettlementRequest
                              ?.totalCodFee || 0}
                          </span>
                        }
                      />
                      <InfoRow
                        label="Merchant Receivable"
                        value={
                          <Text strong>
                            ৳
                            {singleOrderInfo?.data?.shopSettlementRequest
                              ?.totalReceivableAmount || 0}
                          </Text>
                        }
                      />
                      <InfoRow
                        label="Promo Discount"
                        value={
                          <span style={{ color: "#ff4d4f" }}>
                            -৳
                            {singleOrderInfo?.data?.shopSettlementRequest
                              ?.totalPlatformPromoDiscount || 0}
                          </span>
                        }
                      />
                      <InfoRow
                        label="Delivery Discount"
                        value={
                          <span style={{ color: "#ff4d4f" }}>
                            -৳
                            {singleOrderInfo?.data?.shopSettlementRequest
                              ?.totalSellerDeliveryDiscount || 0}
                          </span>
                        }
                      />
                    </div>

                    <Divider style={{ margin: "8px 0" }} />

                    <Row justify="space-between" align="middle">
                      <Text strong style={{ fontSize: 16 }}>
                        Merchant Final Payout
                      </Text>
                      <Text strong style={{ fontSize: 18, color: "#fa8c16" }}>
                        ৳
                        {singleOrderInfo?.data?.shopSettlementRequest
                          ?.totalNetReceivableAmount || 0}
                      </Text>
                    </Row>
                  </Space>
                </Card>
              </div>
            </Col>

            <Col xs={24} xl={16}>
              <Card
                title={
                  <Text strong style={{ fontSize: 16 }}>
                    Order Summary
                  </Text>
                }
                bordered={false}
                style={{
                  borderRadius: 16,
                  boxShadow: "0 6px 24px rgba(0,0,0,0.06)",
                }}
                bodyStyle={{ padding: 0 }}
              >
                {singleOrderInfo?.data?.shopSettlementRequestDetails
                  ?.loading ? (
                  <Loader />
                ) : (
                  <table className={styles.mainTable}>
                    <thead className="bg-white border-b">
                      <tr>
                        <th scope="col">Order ID</th>
                        <th scope="col">Date</th>
                        <th scope="col">Total Amount</th>
                        <th scope="col">Net Receivable</th>
                      </tr>
                    </thead>

                    <tbody>
                      {singleOrderInfo?.data?.shopSettlementRequestDetails
                        ?.length ? (
                        singleOrderInfo?.data?.shopSettlementRequestDetails?.map(
                          (order: any, index: any) => (
                            <tr
                              className="border-t hover:bg-gray-100 cursor-pointer bg-red-50 border-b border-red-50"
                              key={index}
                            >
                              <td>
                                <span className="font-medium text-slate-700">
                                  {order?.orderId}
                                </span>
                              </td>
                              <td>
                                <small
                                  className={`font-capitalize ${
                                    moment().diff(
                                      order?.orderDate,
                                      "minutes",
                                    ) >= 60
                                      ? "text-red-600"
                                      : "text-gray-800"
                                  }`}
                                >
                                  {moment(order?.orderDate).fromNow()}
                                </small>
                                <br />
                                <span className="name">
                                  {moment(order?.orderDate).format("lll")}
                                </span>
                              </td>
                              <td>
                                &#2547;
                                {parseInt(order?.amount || 0)}
                              </td>
                              <td>
                                &#2547;
                                {parseInt(order?.netReceivableAmount || 0)}
                              </td>
                            </tr>
                          ),
                        )
                      ) : (
                        <tr>
                          <td>
                            <Empty />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </Card>
            </Col>
          </Row>
        </div>
      </Content>
    </Layout>
  );
};

export default SettlementDetailsPage;
